import { useState, useEffect, useRef, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from 'src/firebase/firebaseConfig';

export interface AppleMusicSongDetail {
  id: string;
  name: string;
  artistName: string;
  albumName: string;
  durationMs: number;
  isrc: string | null;
  recordLabel: string | null;
  genreNames: string[];
  composerName: string | null;
  releaseDate: string | null;
  trackNumber: number | null;
  discNumber: number | null;
  contentRating: string | null;
  url: string | null;
  previewUrl: string | null;
  artwork: {
    url: string;
    width: number;
    height: number;
  } | null;
}

interface SongsResult {
  success: boolean;
  songs: AppleMusicSongDetail[];
}

/**
 * Fetch Apple Music song details by catalog IDs.
 * Caches results to avoid re-fetching on re-renders.
 */
export function useAppleMusicSongs(songIds: string[]) {
  const [songs, setSongs] = useState<AppleMusicSongDetail[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedIdsRef = useRef<string>('');

  // Stabilize songIds by creating a sorted key - only changes when content changes
  const idsKey = useMemo(() => {
    if (!songIds || songIds.length === 0) return '';
    return [...songIds].sort().join(',');
  }, [songIds]);

  useEffect(() => {
    if (!idsKey) {
      // Only set songs to empty if it's not already empty
      setSongs((prev) => prev.length === 0 ? prev : []);
      return;
    }

    if (fetchedIdsRef.current === idsKey) return;

    let cancelled = false;

    (async () => {
      setIsLoading(true);
      setError(null);

      try {
        const getSongs = httpsCallable<{ songIds: string[] }, SongsResult>(
          functions,
          'getAppleMusicSongs'
        );
        const result = await getSongs({ songIds: idsKey.split(',') });

        if (!cancelled && result.data.success) {
          setSongs(result.data.songs);
          fetchedIdsRef.current = idsKey;
        }
      } catch (err: any) {
        if (!cancelled) {
          console.error('Error fetching Apple Music songs:', err);
          setError(err?.message || 'Failed to load song details');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [idsKey]);

  return { songs, isLoading, error };
}
